/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {loadDevicesByIdentifiers} from '@ion/core/src/services/Connect';
import {BulkActionEntity, Device} from '@ion/core/src/types/entities';
import {createMutationResult} from '../utils/helpers';

export interface UseLoadDevicesParams {
  accountId: number;
  identifiers: string[];
  hierLevel: boolean;
  pgSize: number;
  extension?: string;
  omitExtensions?: boolean;
  entity?: BulkActionEntity;
  identifierType?: string;
};

export type UseLoadDevicesResult = {
  device: Device;
  loadDevices: (params: UseLoadDevicesParams, callbacks) => void;
}

/**
 * The UseLoadDevices hook is used to retrieve devices.
 *
 * ```typescript
 *
 *  import {UseLoadDevices} from '@ion/packages/query/src/hooks';
 *
 *  const {device} = UseLoadDevices({
 *    accountId,
 *    identifiers,
 *    hierLevel,
 *    pgSize,
 *    extension
 *  });
 *
 * ```
 */
export const useLoadDevices = (): UseLoadDevicesResult => {
  const mutation = useMutation(async(params: UseLoadDevicesParams) => {
    const {
      accountId,
      identifiers,
      hierLevel,
      pgSize,
      extension,
      omitExtensions,
      entity,
      identifierType
    } = params;
    const result = await loadDevicesByIdentifiers({
      omitExtensions,
      identifiers,
      accountId,
      hierLevel,
      pgSize,
      extension,
      entity,
      identifierType
    });
    if (result) {
      return result;
    }
    return '';
  });

  return createMutationResult(
    mutation, {
      loadDevices : mutation.mutate
    }
  ) as UseLoadDevicesResult;
};

export default useLoadDevices;
